import React, { useContext, useEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';

import UserAvatar from 'app/components/CommonStyled/UserAvatar';
import usePeopleResetSearch from 'app/components/People/usePeopleResetSearch';
import Tooltip from 'app/components/Tooltip';
import { ResetIcon, SearchIconSmall, Sparkle } from 'app/components/shared/icons';
import { AnimatePresence } from 'framer-motion';
import useBehavioral, { BEHAVIORALS } from 'hooks/useBehavioral';
import useWindowDimensions from 'hooks/useWindowDimensions';
import {
  isNLPLoadingSelector,
  promptSelector,
  setFound,
  setNotFound,
  setPrompt,
  setShowSearchbar,
  unrecognizedKeywordsSelector,
} from 'store/reducers/nlp/nlp-reducer';
import {
  contextSelector,
  emptyAllFilters,
  PEOPLE_CONTEXT,
  previousFiltersLengthSelector,
  setShouldFetch,
  currentStepSelector,
  groupedFiltersSelector,
} from 'store/reducers/people/people-reducer';
import { ThemeProvider } from 'styled-components';
import { arrayToOxford, singularOrPlural } from 'utils';

import { PeopleSearchContext } from '../PeopleSearchContext';
import PeopleSearchBarHistory from './PeopleSearchBarHistory';
import {
  PeopleSearchBarInput,
  PeopleSearchBarMinimalStateContainer,
  PeopleSearchBarMinimalStateGroupContainer,
  PeopleSearchBarMinimalStateGroupLabel,
  PeopleSearchBarMinimalStateGroupsWrapper,
  PeopleSearchBarMinimalStateInformationContainer,
  PeopleSearchBarMinimalStateResetContainer,
  PeopleSearchBarMinimalStateWrapper,
  PeopleSearchBarTabletGroupsContainer,
  PeopleSearchBarTabletGroupsLabel,
  PeopleSearchBarTabletLabel,
} from './styled';

export default function PeopleSearchBar() {
  const dimensions = useWindowDimensions();

  const resetSearch = usePeopleResetSearch();
  const dispatch = useDispatch();
  const collectiveIndexNLPSearchEditBehavioralFunction = useBehavioral(
    BEHAVIORALS.COLLECTIVE_INDEX_NLP_SEARCH_EDIT
  );
  const teamingNLPSearchEditBehavioralFunction = useBehavioral(BEHAVIORALS.TEAMING_NLP_SEARCH_EDIT);
  const context = useContext(PeopleSearchContext);
  const pageContext = useSelector(contextSelector);
  const previousFiltersLength = useSelector(previousFiltersLengthSelector);
  const prompt = useSelector(promptSelector);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const currentStep = useSelector(currentStepSelector);
  const groupedFilters = useSelector(groupedFiltersSelector);
  const isNlpLoading = useSelector(isNLPLoadingSelector);
  const unrecognizedKeywords = useSelector(unrecognizedKeywordsSelector);

  const isTablet = useMemo(() => {
    return dimensions && dimensions.width <= 1180;
  }, [dimensions]);

  useEffect(() => {
    if (isTablet) {
      context.setSearchFocused(true);
      inputRef.current?.focus();
    }
  }, [isTablet]);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    context.setSearchValue(e.target.value);
  };

  const allGroups = useMemo<string[]>(() => {
    return Object.keys(groupedFilters);
  }, [groupedFilters]);

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !context.nlpSearchQuery?.isLoading) {
      context.sendSearch();
      e.preventDefault();
      e.currentTarget.blur();
    }
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLTextAreaElement, Element>) => {
    context.setSearchFocused(false);
    if (e.currentTarget.value === '') {
      dispatch(emptyAllFilters({}));
      dispatch(setPrompt(null));
      dispatch(setFound(null));
      dispatch(setNotFound(null));
      dispatch(setShowSearchbar(true));
      dispatch(setShouldFetch(true));
    }
  };

  useEffect(() => {
    if (context.searchValue !== '' && inputRef.current) {
      inputRef.current.style.height = '30px';
      inputRef.current.style.height = `${inputRef.current.scrollHeight}px`;
    }

    if (context.searchValue === '' && inputRef.current) {
      inputRef.current.style.height = '30px';
    }
  }, [context.searchValue, previousFiltersLength, currentStep, groupedFilters, isNlpLoading]);

  const handleNewSearchClick = () => {
    resetSearch();
  };

  const handleOnFocus = () => {
    if (isTablet) {
      return context.openTabletSearch();
    }

    if (currentStep !== 1) {
      if (pageContext === PEOPLE_CONTEXT.COLLECTIVE_DIRECTORY) {
        collectiveIndexNLPSearchEditBehavioralFunction();
      } else {
        teamingNLPSearchEditBehavioralFunction();
      }
    }

    context.setSearchFocused(true);
  };

  return (
    <ThemeProvider theme={{ currentStep }}>
      <PeopleSearchBarMinimalStateWrapper>
        <PeopleSearchBarMinimalStateContainer>
          <UserAvatar size={40} highlighted />
          <PeopleSearchBarMinimalStateInformationContainer>
            <PeopleSearchBarInput
              data-id="nlp-search-input"
              ref={inputRef}
              value={context.searchValue || ''}
              placeholder="I’m looking for..."
              onChange={handleInputChange}
              onKeyDown={handleKeyPress}
              onFocus={handleOnFocus}
              onBlur={handleInputBlur}
              onInput={() => {
                inputRef.current.style.height = `${inputRef.current.scrollHeight}px`;
              }}
            />
            <AnimatePresence>
              {!isTablet ? (
                <>
                  {allGroups.length > 0 || unrecognizedKeywords.length > 0 ? (
                    <PeopleSearchBarMinimalStateGroupsWrapper>
                      {allGroups.map((group) => (
                        <PeopleSearchBarMinimalStateGroupContainer key={group}>
                          <SearchIconSmall fill={'rgba(23, 28, 51, 0.5)'} width={15} height={15} />
                          <PeopleSearchBarMinimalStateGroupLabel>
                            {group}
                          </PeopleSearchBarMinimalStateGroupLabel>
                        </PeopleSearchBarMinimalStateGroupContainer>
                      ))}
                    </PeopleSearchBarMinimalStateGroupsWrapper>
                  ) : null}
                </>
              ) : (
                <>
                  {allGroups.length > 0 ? (
                    <Tooltip text={arrayToOxford(allGroups)}>
                      <PeopleSearchBarTabletGroupsContainer>
                        <Sparkle width={20} height={20} fill={'rgba(23, 28, 51, 0.5)'} />
                        <PeopleSearchBarTabletGroupsLabel>
                          {allGroups.length}{' '}
                          {singularOrPlural(allGroups.length, 'SOURCE', 'SOURCES')} REFERENCED
                        </PeopleSearchBarTabletGroupsLabel>
                      </PeopleSearchBarTabletGroupsContainer>
                    </Tooltip>
                  ) : null}
                </>
              )}
            </AnimatePresence>
          </PeopleSearchBarMinimalStateInformationContainer>
        </PeopleSearchBarMinimalStateContainer>
        {currentStep !== 1 ? (
          <PeopleSearchBarMinimalStateResetContainer onClick={handleNewSearchClick}>
            <ResetIcon width={30} height={30} fill={'var(--color-primary)'} fillOpacity="1" />
          </PeopleSearchBarMinimalStateResetContainer>
        ) : null}
      </PeopleSearchBarMinimalStateWrapper>
    </ThemeProvider>
  );
}

PeopleSearchBar.Tablet = () => {
  const currentStep = useSelector(currentStepSelector);
  const context = useContext(PeopleSearchContext);

  const handleNewSearchClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    context.closeTabletSearch();
  };

  const handleTabletClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    context.openTabletSearch();
  };

  return (
    <PeopleSearchBarMinimalStateWrapper onClick={handleTabletClick}>
      <PeopleSearchBarMinimalStateContainer>
        <UserAvatar size={40} highlighted />
        <PeopleSearchBarTabletLabel>
          {context.searchValue || 'I’m looking for...'}
        </PeopleSearchBarTabletLabel>
      </PeopleSearchBarMinimalStateContainer>

      {currentStep !== 1 ? (
        <PeopleSearchBarMinimalStateResetContainer onClick={handleNewSearchClick}>
          <ResetIcon width={30} height={30} fill={'var(--color-primary)'} fillOpacity="1" />
        </PeopleSearchBarMinimalStateResetContainer>
      ) : null}
    </PeopleSearchBarMinimalStateWrapper>
  );
};
